import {
  createPendingRefetchResolver,
  LIST_GC_TIME_MS,
  LIST_STALE_TIME_MS,
  useApiClient,
} from '@klareospace/pharos-lib-front-utils';
import type { ImportDetail } from '@klareospace/pharos-lib-types';
import { IMPORT_STATE } from '@klareospace/pharos-lib-types';
import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import { createImportsApi, importKeys } from '../api';

// ─── Types ────────────────────────────────────────────────────────────────────

interface UseImportOptions {
  enabled?: boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Refetch-interval resolver for `useQuery`'s `refetchInterval` option — polls while pending, stops once terminal. */
export const resolveDetailRefetchInterval = createPendingRefetchResolver<ImportDetail>(
  (data) => data?.state === IMPORT_STATE.PENDING,
);

// ─── Hook ─────────────────────────────────────────────────────────────────────

/** Fetches a single import's report detail — polls every 15s while the import is still `pending`. */
export function useImport(id: string, options: UseImportOptions = {}): UseQueryResult<ImportDetail> {
  const { enabled = true } = options;
  const client = useApiClient('deliveries');
  const api = createImportsApi(client);

  return useQuery({
    enabled,
    gcTime: LIST_GC_TIME_MS,
    queryFn: (): Promise<ImportDetail> => api.getImport(id),
    queryKey: importKeys.detail(id),
    refetchInterval: resolveDetailRefetchInterval,
    staleTime: LIST_STALE_TIME_MS,
  });
}
